import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { makeT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

const DEMO_KEY = "mohassib_demo";
export const readDemo = () => {
  try { const d = JSON.parse(sessionStorage.getItem(DEMO_KEY) || "null"); return d && d.exp > Date.now() ? d : null; } catch { return null; }
};

export function AppProvider({ children }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [lang, setLangState] = useState(localStorage.getItem("daftar_lang") || "fr");
  const [companyId, setCompanyIdState] = useState(localStorage.getItem("daftar_company"));
  const [me, setMe] = useState(null);
  const [company, setCompany] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [demo, setDemo] = useState(readDemo);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.title = lang === "ar" ? "محاسب | Mohassib" : "Mohassib | Gestion & facturation électronique";
    localStorage.setItem("daftar_lang", lang);
    window.dispatchEvent(new CustomEvent("daftar-lang", { detail: lang }));
  }, [lang]);

  const api = useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (cfg) => {
      const d = readDemo();
      const tok = d ? d.token : await getToken();
      if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
      if (companyId) cfg.headers["X-Company-Id"] = companyId;
      return cfg;
    });
    return inst;
  }, [getToken, companyId, demo]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCompanyId = useCallback((id) => {
    if (id) localStorage.setItem("daftar_company", id);
    else localStorage.removeItem("daftar_company");
    setCompany(null);
    setCompanyIdState(id);
  }, []);

  const refreshMe = useCallback(async () => {
    const { data } = await api.get("/me");
    setMe(data);
    return data;
  }, [api]);

  const refreshCompany = useCallback(async () => {
    if (!companyId) return null;
    try {
      const { data } = await api.get("/company");
      setCompany(data);
      return data;
    } catch {
      setCompanyId(null);
      return null;
    }
  }, [api, companyId, setCompanyId]);

  useEffect(() => {
    if (demo) { refreshMe(); return; }
    if (isLoaded && isSignedIn) refreshMe();
    if (isLoaded && !isSignedIn) setMe(null);
  }, [isLoaded, isSignedIn, refreshMe, demo]);

  const startDemo = useCallback(async (link) => {
    const { data } = await axios.post(`${API}/demo/session`, null, { params: link ? { link } : {} });
    if (link) localStorage.setItem("mohassib_ref", link);
    const d = { token: data.token, exp: Date.now() + data.expires_in * 1000, company_id: data.company_id, tour: 0, branding: data.branding };
    sessionStorage.setItem(DEMO_KEY, JSON.stringify(d));
    localStorage.setItem("daftar_company", data.company_id);
    setCompanyIdState(data.company_id);
    setDemo(d);
  }, []);
  const exitDemo = useCallback(() => {
    sessionStorage.removeItem(DEMO_KEY);
    localStorage.removeItem("daftar_company");
    setDemo(null); setMe(null); setCompany(null); setCompanyIdState(null);
  }, []);
  useEffect(() => {
    if (!demo) return undefined;
    const id = setTimeout(() => { exitDemo(); window.location.assign("/?demo_expired=1"); }, demo.exp - Date.now());
    return () => clearTimeout(id);
  }, [demo, exitDemo]);

  useEffect(() => {
    if (!me) return;
    const inList = me.companies.some((c) => c.id === companyId);
    if (!companyId || (!inList && !me.is_superadmin)) {
      if (me.companies[0]) setCompanyId(me.companies[0].id);
      else if (companyId) setCompanyId(null);
    }
  }, [me, companyId, setCompanyId]);

  useEffect(() => {
    if (me && companyId) refreshCompany();
  }, [me, companyId, refreshCompany]);

  const download = useCallback(async (url, filename, open = true) => {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    if (open && res.data.type === "application/pdf") window.open(href, "_blank");
    else {
      const a = document.createElement("a");
      a.href = href;
      a.download = filename || "export";
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  }, [api]);

  const perms = useMemo(() => company?.perms || {}, [company]);
  const can = useCallback((m, w = false) => (w ? perms[m] === "rw" : !!perms[m]), [perms]);
  const readOnly = company?.sub_state === "expired";

  const value = { api, lang, setLang: setLangState, t: makeT(lang), me, refreshMe, company, refreshCompany, companyId,
                  setCompanyId, can, readOnly, download, creatingNew, setCreatingNew, demo, startDemo, exitDemo };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useApp = () => useContext(Ctx);
